import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { getSubscriberStatus } from 'utils/common';
import { ASSET_PREFIX, SubscriberStatus } from 'utils/constants';

import { AccountBox } from 'components/account/AccountBox';
import MyImpactAccountSection from 'components/account/MyImpactAccountSection';
import { NeedHelpSection } from 'components/account/NeedHelpSection';
import { randomPhrases } from 'components/landing/StrongerVariant/PrizesSection/WelcomeBackSection';
import AccountsMenuStatsSection from 'components/layout/AccountsMenuStatsSection';
import PageTitle from 'components/PageTitle';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import ProfilePhoto from 'components/ProfilePhoto';
import Separator from 'components/Separator';
import StandardCTA from 'components/StandardCTA';
import Subheading, { SubheadingColourScheme } from 'components/Subheading';
import { SubscriptionStateInfo } from 'components/subscriptions/info-message/SubscriptionStateInfo';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import { useAuthRedirectEffect } from 'hooks/account/useAuthRedirect';

const Index = observer(
  ({
    redeemTokens,
  }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { displayToast } = useUIStore();
    const {
      currentUser,
      updateCurrentUser,
      logout,
      activeSubscriptions,
      isOperator,
      claimablePrizesCount,
      subscriptionStatusInfo,
    } = useUserStore();
    const treesPlanted = currentUser?.treesPlanted;

    const [loading, setLoading] = useState(true);

    const [latestEntriesExpanded, setLatestEntriesExpanded] = useState(true);
    const [myImpactExpanded, setMyImpactExpanded] = useState(false);

    const [randomPhrase, setRandomPhrase] = useState<string | null>(null);
    useAuthRedirectEffect('/account');

    useEffect(() => {
      (async () => {
        setRandomPhrase(
          randomPhrases[Math.floor(Math.random() * randomPhrases.length)]
        );
        setLoading(false);

        updateCurrentUser();
      })();
    }, []);

    // Forward to Tokens page if visiting with appropriate query parameter.
    useEffect(() => {
      (async () => {
        if (redeemTokens) {
          Router.push('/token-town');
        }
      })();
    }, [redeemTokens]);

    console.log('activeSubscriptions', activeSubscriptions);

    if (loading || !currentUser || !activeSubscriptions) {
      return (
        <MainLayout additionalClassName="accountPage">
          <NextSeo
            title="Account | Win Epic Prizes | DAYMADE"
            openGraph={{
              title: 'Account | Win Epic Prizes | DAYMADE',
            }}
          />

          <PageTitle
            heroBackgroundImage={`${ASSET_PREFIX}/images/account/page-title-background.svg`}
          >
            My <strong>account</strong>
          </PageTitle>

          <PlainLoadingSpinner style={{ margin: '10em' }} />
        </MainLayout>
      );
    }

    return (
      <MainLayout additionalClassName="accountPage">
        <NextSeo
          title="Account | Win Epic Prizes | DAYMADE"
          openGraph={{
            title: 'Account | Win Epic Prizes | DAYMADE',
          }}
        />

        <PageTitle
          heroBackgroundImage={`${ASSET_PREFIX}/images/account/page-title-background.svg`}
        >
          My <strong>account</strong>
        </PageTitle>

        <PaddedMaxWidthContainer>
          <div className="w-full flex flex-col md:flex-row gap-2">
            <div className="w-full md:w-1/2">
              <AccountBox
                id="profile-photo-and-stats"
                collapsible={false}
                expanded={false}
              >
                <ProfilePhoto scale={1.5} />
                {/* Scale up the profile photo when the player has more entries */}
                <div className="playerName">Hey, {currentUser?.firstName}!</div>
                <div className="randomPhrase">{randomPhrase}</div>
                <Separator
                  style={{
                    margin: `${7 + 1}% 0`,
                  }}
                />
                <AccountsMenuStatsSection />
              </AccountBox>
            </div>

            <div className="w-full md:w-1/2 gap-2 flex flex-col">
              <AccountBox
                id="latest-entries-box"
                collapsible
                expanded={latestEntriesExpanded}
                setExpanded={() => {
                  if (!latestEntriesExpanded) {
                    setMyImpactExpanded(false);
                  }
                  setLatestEntriesExpanded(!latestEntriesExpanded);
                }}
                title="Current entries"
                icon={`${ASSET_PREFIX}/images/account/my-entries-icon.svg`}
              >
                <SubscriptionStateInfo />
              </AccountBox>

              {treesPlanted && treesPlanted > 0 ? (
                <AccountBox
                  id="my-impact-box"
                  collapsible
                  expanded={myImpactExpanded}
                  setExpanded={() => {
                    if (!myImpactExpanded) {
                      setLatestEntriesExpanded(false);
                    }
                    setMyImpactExpanded(!myImpactExpanded);
                  }}
                  title="My impact"
                  icon={`${ASSET_PREFIX}/images/account/my-impact-icon.svg`}
                >
                  <MyImpactAccountSection treesPlanted={treesPlanted} />
                </AccountBox>
              ) : null}
            </div>
          </div>

          {[SubscriberStatus.LITE, SubscriberStatus.FREEMIUM].includes(
            getSubscriberStatus(subscriptionStatusInfo) as typeof SubscriberStatus.LITE | typeof SubscriberStatus.FREEMIUM
          ) && (
            <>
              <Separator />

              <div className="upgradeToPremiumSection fullWidth flexCentered">
                <div>
                  <Subheading
                    colourScheme={SubheadingColourScheme.GREY_TEXT_WHITE_SHADOW}
                    style={{ fontSize: '2.75em' }}
                  >
                    Upgrade to
                  </Subheading>
                  <Subheading style={{ fontSize: '3.7em' }}>
                    <strong>Premium</strong>
                  </Subheading>
                </div>
                <div className="upgradeNowBulletPoints">
                  <ul>
                    <li>
                      <strong>1 in 3</strong> chances of winning
                    </li>
                    <li>
                      Plant a tree <strong>every week</strong>
                    </li>
                    <li>
                      Choose from <strong>400+ prizes</strong>
                    </li>
                  </ul>
                </div>
                <StandardCTA overrideLabel="Upgrade now" />
              </div>
            </>
          )}

          <Separator />
          <div className="otherStuffSection fullWidth flexCentered">
            <Subheading style={{ fontSize: '2.6em' }}>
              Other <strong>stuff</strong>
            </Subheading>

            <div className="otherStuffItemsContainer fullWidth flexCentered">
              {isOperator && (
                <AccountBox
                  additionalClassName="adminConsole"
                  title="Admin console"
                  href="/admin"
                  icon={''}
                  collapsible={false}
                  expanded={false}
                  newTab={true}
                />
              )}
              <AccountBox
                title="Draw Results"
                icon={`${ASSET_PREFIX}/images/account/prizes.svg`}
                href="/results"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="manageSubscription"
                title="Manage subscription"
                icon={`${ASSET_PREFIX}/images/account/manage-subscription-icon.svg`}
                href="/account/subscriptions"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="referAFriend"
                title="REFER & EARN £12"
                icon={`${ASSET_PREFIX}/images/account/refer-a-friend-icon.svg`}
                href="/account/referrals"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="myPrizes"
                title="My prizes"
                icon={`${ASSET_PREFIX}/images/account/my-prizes-icon.svg`}
                href="/account/my-prizes"
                badgeText={
                  claimablePrizesCount > 0 ? 'Unclaimed Prize' : undefined
                }
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                title="Entry history"
                icon={`${ASSET_PREFIX}/images/account/entry-history-icon.svg`}
                href="/account/entries"
                additionalClassName={''}
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="myDiscounts"
                title="My discounts"
                icon={`${ASSET_PREFIX}/images/account/my-discounts-icon.svg`}
                href="/my-discounts"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="myDetails"
                title="My details"
                icon={`${ASSET_PREFIX}/images/account/my-details-icon.svg`}
                href="/account/change-details"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="myWishlist"
                title="My wishlist"
                icon={`${ASSET_PREFIX}/images/account/my-wishlist-icon.svg`}
                href="/account/wish-list"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="emailPreferences"
                title="Email preferences"
                icon={`${ASSET_PREFIX}/images/account/email-preferences-icon.svg`}
                href="/account/email-preferences"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="changePassword"
                title="Change password"
                icon={`${ASSET_PREFIX}/images/account/change-password-icon.svg`}
                href="/account/change-password"
                collapsible={false}
                expanded={false}
              />

              <AccountBox
                additionalClassName="signOut"
                title="Sign out"
                icon={`${ASSET_PREFIX}/images/account/sign-out-icon.svg`}
                onClick={() => {
                  logout();
                  displayToast({
                    title: 'You have successfully logged out.',
                    timeout: 3500,
                  });
                  Router.push('/');
                }}
                collapsible={false}
                expanded={false}
              />
            </div>
          </div>
        </PaddedMaxWidthContainer>

        <NeedHelpSection email={currentUser?.emailAddress} />

        <style jsx>
          {`
            :global(.accountPage .separator) {
              // margin: 2.5em 0;
            }

            .profileSection {
              display: grid;
              grid-template-columns: 1fr;
              grid-gap: 0.5em 0;
              justify-items: flex-start;
            }

            .playerName {
              font-size: 1.5em;
              font-weight: 700;
              margin-top: 1em;
            }

            .randomPhrase {
              font-size: 1.1em;
              font-weight: 600;
              margin-top: 1em;
            }

            .actions {
              width: 100%;
              margin: 2em 0 0.5em;
              padding: 0 0.25em;
            }

            .actions * {
              text-decoration: underline;
              font-weight: 700;
              cursor: pointer;
            }

            .upgradeToPremiumSection,
            .otherStuffSection {
              padding: 0.5em 0 1em;
            }

            :global(.upgradeToPremiumSection .subheading) {
              margin: 0;
            }

            .upgradeNowBulletPoints {
              margin: 1em 0 0.5em;
            }

            ul {
              margin-block-start: 0;
              padding-inline-start: 0.9em;
            }

            li {
              margin: 0.8em 0;
              font-size: 1.1em;
              font-weight: 600;
            }

            li strong {
              font-weight: 800;
            }

            :global(.upgradeToPremiumSection .standardCTA) {
              min-width: 250px;
              margin-top: 1.5em;
            }

            :global(.accountBox .cardList) {
              margin-top: 0;
            }

            :global(.accountBox .ticket) {
              justify-content: flex-start;
              margin-top: 0;
            }

            :global(.profileSection #latest-entries-box .childrenContainer) {
              padding-left: 1.5em;
              padding-right: 1.5em;
            }

            .otherStuffItemsContainer {
              display: grid;
              grid-template-columns: 1fr;
              grid-gap: 0.5em;
              justify-items: center;
            }

            @media (min-width: 750px) {
              .profileSection {
                grid-template-columns: 1fr 1fr;
                grid-template-rows: 1fr;
                grid-gap: 10px;
                align-items: flex-start;
              }

              :global(.profileSection #profile-photo-and-stats) {
                grid-row: 1 / 3;
                padding-bottom: ${2 + 1}%;
              }

              :global(.profileSection #latest-entries-box .childrenContainer) {
                grid-row: 1;
                grid-column: 2;
                padding-left: 2em;
                padding-right: 2em;
              }

              :global(.profileSection #my-impact-box) {
                grid-row: 2;
                grid-column: 2;
                opacity: 1;
              }

              .upgradeToPremiumSection {
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                padding: 1em 0;
                flex-wrap: wrap;
              }

              :global(.upgradeToPremiumSection .subheading) {
                max-width: 260px;
                text-align: left;
                font-size: 2.5em;
              }

              .upgradeNowBulletPoints {
                margin: 0;
              }

              :global(.upgradeToPremiumSection .standardCTA) {
                margin-left: 1em;
                margin-top: 0;
                min-width: 200px;
              }

              .otherStuffItemsContainer {
                margin: 1em 0 2em;
                grid-template-columns: 1fr 1fr;
                grid-gap: 0.75em 0.5em;
              }

              :global(.profileSection .accountBox) {
                width: 100%;
                max-width: unset;
              }
            }

            @media (min-width: 775px) {
              :global(.upgradeToPremiumSection .subheading) {
                max-width: 265px;
                font-size: 3em;
              }
            }

            @media (min-width: 900px) {
              :global(.accountPage .separator) {
                // margin: 4em 0;
              }

              .upgradeToPremiumSection {
                padding: 0;
              }

              :global(.upgradeToPremiumSection .subheading) {
                max-width: 310px;
                font-size: 3.5em;
              }

              .upgradeNowBulletPoints {
                font-size: 1.15em;
              }

              :global(.upgradeToPremiumSection .standardCTA) {
                margin-left: 1em;
                margin-top: 0;
                min-width: 250px;
                font-size: 1.2em;
              }
            }
          `}
        </style>
      </MainLayout>
    );
  }
);

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const redeemTokens = ctx.query.action === 'redeem-tokens';

  return {
    props: {
      redeemTokens,
    },
  };
};

export default Index;
